import { DEFAULT_VOICE_TRIAL_DURATION_HOURS, MICRO_USD_PER_CREDIT } from '@/lib/billing/constants'
import type { PlanFormData } from './types'

export type SectionId = 'overview' | 'credits' | 'voice' | 'resources' | 'shopify' | 'lifecycle' | 'pricing'
export type FieldKey = keyof PlanFormData
export type NumericKey = { [K in FieldKey]: PlanFormData[K] extends number | null ? K : never }[FieldKey]
export interface NumericField {
  section: SectionId
  key: NumericKey
  label: string
  hint: string
  unit?: string
  scale?: number
  min?: number
  max?: number
  step?: number
  empty?: string
  zero?: string
  valueLabel?: string
}

export const SECTIONS: { id: SectionId; title: string; detail: string; description: string }[] = [
  {
    id: 'overview',
    title: 'Overview',
    detail: 'Identity & presentation',
    description: 'Give this plan a clear identity and decide where it is available.',
  },
  {
    id: 'pricing',
    title: 'Pricing',
    detail: 'Subscription & usage',
    description: 'Set the subscription price, configure its payment provider and define usage charges.',
  },
  {
    id: 'credits',
    title: 'Credits & spending',
    detail: 'Grants, top-ups & caps',
    description: 'Separate what customers receive from what they can spend.',
  },
  {
    id: 'voice',
    title: 'Voice & campaigns',
    detail: 'Access & capacity',
    description: 'Set voice access and capacity together, with campaign controls nearby.',
  },
  {
    id: 'resources',
    title: 'Users & integrations',
    detail: 'Users, knowledge & CRM',
    description: 'Choose the resources available to each tenant on this plan.',
  },
  {
    id: 'shopify',
    title: 'Shopify & analytics',
    detail: 'Stores & insights',
    description: 'Store connectivity and analytics access have separate limits.',
  },
  {
    id: 'lifecycle',
    title: 'Lifecycle',
    detail: 'Expiry & grace',
    description: 'Set when tenant access ends and how long a failed payment is tolerated.',
  },
]

export const FIELD_LABELS = {
  name: 'Internal identifier',
  display_name: 'Plan name',
  status: 'Visibility',
  benefits: 'Pricing-card benefits',
  billing_channel: 'Payment channel',
  shopify_plan_handle: 'Shopify plan handle',
  voice_included: 'Voice included',
  voice_trial_enabled: 'Voice trial available',
  campaigns_enabled: 'Campaign access',
  campaigns_trial_enabled: 'Campaigns during subscription trial',
  plan_credits_expire: 'Plan credits expire when the billing period ends',
  // History only.
  features: 'Feature flags',
  billing_intervals: 'Billing intervals',
} as const

export const NUMERIC_FIELDS = {
  price: {
    section: 'pricing',
    key: 'price_monthly_cents',
    label: 'Monthly price',
    hint: 'The subscription price. Credits are configured separately.',
    unit: 'USD / month',
    scale: 100,
    step: 0.01,
  },
  order: {
    section: 'overview',
    key: 'sort_order',
    label: 'Display order',
    hint: 'Lower numbers appear first.',
  },
  initial: {
    section: 'credits',
    key: 'initial_balance_microusd',
    label: 'Starting credits',
    hint: 'A one-time grant when a tenant is first assigned this plan.',
    unit: 'credits',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
  },
  recurring: {
    section: 'credits',
    key: 'payment_to_balance_microusd',
    label: 'Credits per paid month',
    hint: 'Independent of the plan price. Annual payments grant 12 months at once.',
    unit: 'credits / month',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
  },
  payments: {
    section: 'credits',
    key: 'payment_to_balance_count',
    label: 'Eligible payments',
    hint: 'All payments, or only the first N payments. Counts payments, not months.',
    min: 1,
    empty: 'Every payment',
    valueLabel: 'First N payments',
  },
  topup: {
    section: 'credits',
    key: 'min_topup_cents',
    label: 'Minimum custom top-up',
    hint: 'Minimum custom Stripe purchase; Shopify offers its own fixed packages.',
    unit: 'USD',
    scale: 100,
    step: 0.01,
  },
  bonus: {
    section: 'credits',
    key: 'topup_bonus_percent',
    label: 'Top-up bonus',
    hint: 'Extra credits added to a purchased top-up.',
    unit: '%',
  },
  daily: {
    section: 'credits',
    key: 'daily_spending_limit_microusd',
    label: 'Daily spending cap',
    hint: 'A usage cap, not a limit on the wallet balance.',
    unit: 'credits',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    empty: 'No cap',
    zero: 'Block new usage',
  },
  monthly: {
    section: 'credits',
    key: 'monthly_spending_limit_microusd',
    label: 'Monthly spending cap',
    hint: 'Usage already in progress can complete and cross the cap.',
    unit: 'credits',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    empty: 'No cap',
    zero: 'Block new usage',
  },
  agents: {
    section: 'voice',
    key: 'voice_agents_limit',
    label: 'Voice agents',
    hint: 'A finite capacity. With voice access or a trial, zero still permits a first agent.',
  },
  concurrency: {
    section: 'voice',
    key: 'voice_concurrency_limit',
    label: 'Simultaneous calls',
    hint: 'Voice pool capacity reserved for this tenant.',
  },
  campaigns: {
    section: 'voice',
    key: 'campaigns_limit',
    label: 'Campaign count',
    hint: 'Retained when campaign access is disabled.',
    empty: 'Unlimited',
    zero: 'None',
  },
  numbers: {
    section: 'voice',
    key: 'pbx_numbers_limit',
    label: 'Phone numbers',
    hint: 'Maximum active PBX numbers.',
    empty: 'Unlimited',
    zero: 'None',
  },
  flows: {
    section: 'voice',
    key: 'pbx_flows_limit',
    label: 'Call routing flows',
    hint: 'Maximum PBX routing configurations.',
    empty: 'Unlimited',
    zero: 'None',
  },
  users: {
    section: 'resources',
    key: 'users_limit',
    label: 'Users',
    hint: 'Maximum users in a tenant.',
    empty: 'Unlimited',
    zero: 'None',
  },
  knowledge: {
    section: 'resources',
    key: 'knowledge_bases_limit',
    label: 'Knowledge-base count',
    hint: 'Admin limit. New plans default to 500; customer plan cards show storage in MB only.',
    empty: 'Unlimited',
    zero: 'None',
  },
  size: {
    section: 'resources',
    key: 'knowledge_bases_size_mb_limit',
    label: 'Knowledge storage',
    hint: 'Combined size of knowledge bases.',
    unit: 'MB',
    empty: 'Unlimited',
    zero: 'None',
  },
  crm: {
    section: 'resources',
    key: 'crm_integrations_limit',
    label: 'CRM connections',
    hint: 'Maximum connected CRM integrations.',
    empty: 'Unlimited',
    zero: 'None',
  },
  stores: {
    section: 'shopify',
    key: 'shopify_stores_limit',
    label: 'Connected stores',
    hint: 'Counts store domains. Available on either billing channel.',
    empty: 'Unlimited',
    zero: 'None',
  },
  channels: {
    section: 'shopify',
    key: 'marketing_channels_limit',
    label: 'Marketing channels',
    hint: 'Stored setting; creation does not currently enforce this limit.',
    empty: 'Unlimited',
    zero: 'None',
  },
  insights: {
    section: 'shopify',
    key: 'shopify_insights_trial_duration_hours',
    label: 'Insights access',
    hint: 'Trial starts on first access, once per tenant. Revenue and order totals remain available.',
    unit: 'hours',
    min: 1,
    empty: 'Included without a time limit',
    valueLabel: 'Trial duration',
  },
  voiceDuration: {
    section: 'voice',
    key: 'voice_trial_duration_hours',
    label: 'Voice trial duration',
    hint: `Measured from activation. An unset duration currently defaults to ${DEFAULT_VOICE_TRIAL_DURATION_HOURS} hours.`,
    unit: 'hours',
    empty: `Default: ${DEFAULT_VOICE_TRIAL_DURATION_HOURS} hours (${DEFAULT_VOICE_TRIAL_DURATION_HOURS / 24} days)`,
  },
  voiceBudget: {
    section: 'voice',
    key: 'voice_trial_budget_microusd',
    label: 'Voice trial budget',
    hint: 'A cap on wallet spending, not an additional credit grant. Zero also means no budget cap.',
    unit: 'credits',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    empty: 'No budget cap',
  },
  expiry: {
    section: 'lifecycle',
    key: 'expiry_days',
    label: 'Account access duration',
    hint: 'After this duration the tenant is locked and asked to upgrade. This does not expire credits.',
    unit: 'days',
    min: 1,
    empty: 'No account expiry',
    valueLabel: 'Expires after',
  },
  grace: {
    section: 'lifecycle',
    key: 'grace_period_days',
    label: 'Failed-payment grace period',
    hint: 'Subscription grace after a payment failure; not extra time to spend an empty wallet.',
    unit: 'days',
  },
  textMultiplier: {
    section: 'pricing',
    key: 'text_cost_multiplier',
    label: 'Text cost multiplier',
    hint: 'Also used for knowledge-base embeddings.',
    min: 1,
    step: 0.1,
    unit: '× cost',
  },
  textMinimum: {
    section: 'pricing',
    key: 'text_min_charge_microusd',
    label: 'Text minimum charge',
    hint: 'Per text request.',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    unit: 'credits',
  },
  textRounding: {
    section: 'pricing',
    key: 'text_rounding_decimals',
    label: 'Text rounding',
    hint: 'USD decimals: 2 = 1-credit steps; 4 = 0.01-credit steps.',
    max: 6,
  },
  voiceMultiplier: {
    section: 'pricing',
    key: 'voice_cost_multiplier',
    label: 'Voice cost multiplier',
    hint: 'Applied to voice provider costs.',
    min: 1,
    step: 0.1,
    unit: '× cost',
  },
  voiceMinimum: {
    section: 'pricing',
    key: 'voice_min_charge_microusd',
    label: 'Voice minimum charge',
    hint: 'Per voice call.',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    unit: 'credits',
  },
  voiceRounding: {
    section: 'pricing',
    key: 'voice_rounding_decimals',
    label: 'Voice rounding',
    hint: 'USD decimal precision, from 0 to 6.',
    max: 6,
  },
  embeddingMinimum: {
    section: 'pricing',
    key: 'embedding_min_charge_microusd',
    label: 'Embedding minimum charge',
    hint: 'Per knowledge-base embedding operation.',
    scale: MICRO_USD_PER_CREDIT,
    step: 0.0001,
    unit: 'credits',
  },
  embeddingRounding: {
    section: 'pricing',
    key: 'embedding_rounding_decimals',
    label: 'Embedding rounding',
    hint: 'USD decimal precision, from 0 to 6.',
    max: 6,
  },
} satisfies Record<string, NumericField>
